/* eslint-disable react-hooks/exhaustive-deps */
import './App.less';

import { useColorMode } from '@tonic-one/react';
import { Layout } from 'antd';
import { useEffect, useState } from 'react';

import { AccountApi, GlobalApi, OrgApi } from '@Apis';
import CasHeader from './components/CasHeader';
import CasSider from './components/Sider';
import { useAccountStore } from './models/useAccountModel';
import { useLicenseStore } from './models/useLicenseModel';
import { useLoginUserStore } from './models/useLoginUserModel';
import { useOrgStore } from './models/useOrgModel';
import { usePermissionStore } from './models/usePermissionModel';
import CasRouter from './Router';
import { LoginUser } from './types';
import setupPendo from './utils/setupPendo';
import GlobalStyles from './GlobalStyles';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
import { timezoneToUtcOffsetZ } from '@Utils/time';
import isJSON from 'validator/lib/isJSON';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import i18n from 'i18next';
import { useSiderWidthStore } from '@Models/useSiderWidthModel';
import { UpgradeStatus } from './pages/Welcome/EmailAndCollaboration';
import { isInUIC, setFavicon } from '@Utils/common';
import UpgradeBanner from '@Components/Banner/UpgradeBanner';
import { GlobalCustomEvent } from './utils/enums';
import CSpinner from '@Components/CSpinner';
import config from './config';
import { WhatsNew } from './components/WhatsNewModal';
import { useLayoutStore } from '@Models/useLayoutModel';

setupPendo();

const shouldHideBanner = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('hideBanner') === 'true';
};

const shouldHideHeader = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('hideHeader') === 'true';
};

const shouldHideSider = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('hideSider') === 'true';
};

export function App() {
  const [colorMode] = useColorMode();
  const { Content } = Layout;
  const [loading, setLoading] = useState(false);
  const { fetchLicense } = useLicenseStore();
  const { setPermissions, setManagementScopeIds } = usePermissionStore();
  const loginUserModel = useLoginUserStore();
  const { setGroupCompanyGuid } = useAccountStore();
  const { setUtcOffset } = useUtcOffsetStore();
  const { setSiderWidth } = useSiderWidthStore();
  const { setSelectedOrgGlobalSetting } = useGlobalSettingStore();
  const { orgId, setOrgs } = useOrgStore();
  const { layoutHeight, setHeaderHeight } = useLayoutStore();
  const parseLoginUser = async () => {
    const user = (await AccountApi.getLoginUser()).data as LoginUser;
    if (isInUIC()) {
      loginUserModel.setUser({
        ...user,
        accountName: localStorage.getItem('uic_companyName') || '',
      });
    } else {
      loginUserModel.setUserModifySession(user);
    }
    setGroupCompanyGuid(user.groupCompanyGuid);
    sessionStorage.setItem('casGroupCompanyGuid', user.groupCompanyGuid);
  };

  const parseOrgs = async () => {
    const orgs = await OrgApi.getOrgs();
    setOrgs(orgs);
  };

  const parsePermissions = async () => {
    const permissionsRes = ((await GlobalApi.getPermissions()) as any).data;
    setPermissions(permissionsRes.permissions);
    setManagementScopeIds(permissionsRes.orgIds);
  };

  const resetUtcOffset = () => {
    const timeZoneString = localStorage.getItem('uic_consoleSettings');
    if (timeZoneString && isJSON(timeZoneString)) {
      const timeZone = JSON.parse(timeZoneString).timezone_identifier;
      const offset = timezoneToUtcOffsetZ(timeZone);
      setUtcOffset(offset);
    }
  };

  const resetLanguage = () => {
    const language = localStorage.getItem('uic_language');
    if (!language) return;
    localStorage.setItem('i18nextLng', language);
    i18n.changeLanguage(language).then();
  };

  const isToWelcome = async () => {
    const isWelcomeRoute = window.location.href.indexOf('/welcome') > -1;
    if (isWelcomeRoute) {
      return false;
    }
    const casIsUpgrade = sessionStorage.getItem('casIsUpgrade');
    if (casIsUpgrade !== 'true') {
      const clpCompanyId = localStorage.getItem('uic_companyGUID') || '';
      if (clpCompanyId) {
        const v1StepUpStatus: {
          isProvisioned: boolean;
          status: number;
          licenseActive: boolean;
        } = (await AccountApi.getV1EASSetupStatus(clpCompanyId)).data;
        if (
          [
            UpgradeStatus.NewProvisionUpgradeDone,
            UpgradeStatus.UpgradeDone,
          ].includes(v1StepUpStatus.status) &&
          v1StepUpStatus.licenseActive
        ) {
          sessionStorage.setItem('casIsUpgrade', 'true');
          return false;
        } else {
          (window.parent as any).APP.$router.push(
            `${config.ui.uicPrefix}/welcome`
          );
          return true;
        }
      }
    }
    return false;
  };
  useEffect(() => {
    setFavicon();
    if (shouldHideHeader()) {
      setHeaderHeight(0);
    }
    (async function doCall() {
      setLoading(true);
      try {
        if (isInUIC()) {
          const isToWelcomePage = await isToWelcome();
          if (isToWelcomePage) return;
          setSiderWidth(0);
          resetUtcOffset();
          resetLanguage();

          if (window.location.href.includes('/welcome')) {
            return;
          }
        }
        const results = await Promise.allSettled([
          parseOrgs(),
          parseLoginUser(),
          parsePermissions(),
          fetchLicense(),
        ]);
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Error in operation ${index + 1}:`, result.reason);
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const isWelcomeRoute = window.location.href.indexOf('/welcome') > -1;
    if (
      isWelcomeRoute ||
      (isInUIC() && sessionStorage.getItem('casIsUpgrade') !== 'true')
    ) {
      return;
    }
    const fetchGlobalSetting = async () => {
      try {
        const globalSettingRes = (await GlobalApi.getGlobalSettings(orgId))
          .data;
        if (isInUIC()) {
          //always show the sandbox feature
          globalSettingRes['urlSandboxFeatureShow'] = true;
          globalSettingRes['gmailInlineFeatureShow'] = true;
          globalSettingRes['exchangeInlineFeatureShow'] = true;
        }
        console.log('globalSettingRes', globalSettingRes);
        setSelectedOrgGlobalSetting(globalSettingRes);
      } catch (error) {
        console.error(error);
        setSelectedOrgGlobalSetting({});
      }
    };
    fetchGlobalSetting();

    window.addEventListener(
      GlobalCustomEvent.FreshGlobalSetting,
      fetchGlobalSetting
    );
    return () => {
      window.removeEventListener(
        GlobalCustomEvent.FreshGlobalSetting,
        fetchGlobalSetting
      );
    };
  }, [orgId]);

  return (
    //for v1 app,it should be dark mode
    <>
      <GlobalStyles />
      {!shouldHideBanner() && <UpgradeBanner />}
      <Layout className={colorMode ? 'dark' : 'light'}>
        {!shouldHideSider() && <CasSider />}
        <Layout>
          {!shouldHideHeader() && <CasHeader />}
          {loading ? (
            <CSpinner />
          ) : (
            <Layout
              className="app-body"
              style={{
                height: layoutHeight,
              }}
            >
              <Content
                className="app-content"
                style={{
                  minHeight: layoutHeight,
                }}
              >
                <CasRouter />
              </Content>
            </Layout>
          )}
        </Layout>
      </Layout>
      <WhatsNew />
    </>
  );
}

export default App;
